import React, { useEffect, useMemo, useState } from "react";
import L from "leaflet";
import { Circle, MapContainer, Marker, Popup, Polyline, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { CloudRain, Compass, Expand, Layers, MapPin, Waves, Wind, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getPublicWeatherTranslations } from "@/i18n";

const layers = [
  { id: "wind", label: "Wind", icon: Wind },
  { id: "swell", label: "Waves", icon: Waves },
  { id: "rain", label: "Radar", icon: CloudRain },
  { id: "lightning", label: "Thunder", icon: Zap },
  { id: "pressure", label: "Pressure", icon: Compass },
];

function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function destinationPoint(lat, lon, distanceKm, bearingDeg) {
  const radiusKm = 6371;
  const bearing = (bearingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lon1 = (lon * Math.PI) / 180;
  const angularDistance = distanceKm / radiusKm;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
      Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearing)
  );
  const lon2 = lon1 + Math.atan2(
    Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1),
    Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
  );
  return [(lat2 * 180) / Math.PI, (lon2 * 180) / Math.PI];
}

function buildFlowLines(center, bearing, count, spreadKm, lengthKm) {
  const [lat, lon] = center;
  const perpendicular = bearing + 90;
  return Array.from({ length: count }, (_, index) => {
    const offset = (index - (count - 1) / 2) * spreadKm;
    const midpoint = destinationPoint(lat, lon, offset, perpendicular);
    const start = destinationPoint(midpoint[0], midpoint[1], lengthKm / 2, bearing + 180);
    const end = destinationPoint(midpoint[0], midpoint[1], lengthKm / 2, bearing);
    return { start, end };
  });
}

function directionIcon(label, color, bearing, size = 28) {
  return L.divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border-radius:9999px;background:${color};border:2px solid white;box-shadow:0 10px 24px rgba(15,23,42,.28);display:flex;align-items:center;justify-content:center;color:white;font-weight:900;font-size:13px;transform:rotate(${bearing}deg);">${label}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function labelIcon(text, color = "#0f172a") {
  return L.divIcon({
    className: "",
    html: `<div style="white-space:nowrap;border-radius:9999px;background:rgba(15,23,42,.82);color:white;border:1px solid rgba(255,255,255,.35);padding:5px 9px;font-size:11px;font-weight:900;box-shadow:0 10px 24px rgba(15,23,42,.25);"><span style="color:${color}">●</span> ${text}</div>`,
    iconSize: [110, 28],
    iconAnchor: [55, 14],
  });
}

function MapResizeFix({ center }) {
  const map = useMap();

  useEffect(() => {
    map.setView(center, 7, { animate: false });
    const timers = [80, 250, 700, 1400].map((delay) => window.setTimeout(() => map.invalidateSize({ pan: false }), delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [map, center]);

  return null;
}

function WeatherDataLayer({ activeLayer, center, metrics, copy }) {
  const [lat, lon] = center;
  const windFlow = (metrics.windDirection + 180) % 360;
  const windLines = buildFlowLines(center, windFlow, 6, 11, 58);
  const swellLines = buildFlowLines(center, metrics.swellDirection, 4, 13, 48);
  const windWeight = Math.max(2, Math.min(7, metrics.windSpeed / 5));
  const rainRadius = Math.max(26000, Math.min(82000, 26000 + metrics.precipitation * 18000));
  const waveRadius = Math.max(28000, Math.min(88000, 28000 + metrics.waveHeight * 18000));
  const thunderRisk = Math.min(100, Math.round(metrics.precipitation * 28 + metrics.gustSpeed * 1.4));
  const pressureTone = metrics.pressure >= 1018 ? "#2563eb" : metrics.pressure <= 1006 ? "#dc2626" : "#7c3aed";

  if (activeLayer === "wind") {
    return (
      <>
        {windLines.map((line, index) => (
          <React.Fragment key={`wind-${index}`}>
            <Polyline positions={[line.start, line.end]} pathOptions={{ color: "#0284c7", weight: windWeight, opacity: 0.78, dashArray: "10 10" }} />
            <Marker position={line.end} icon={directionIcon("➤", "#0284c7", windFlow, 24)} />
          </React.Fragment>
        ))}
        <Circle center={center} radius={Math.max(22000, metrics.gustSpeed * 1250)} pathOptions={{ color: "#0ea5e9", fillColor: "#38bdf8", fillOpacity: 0.12, weight: 2 }} />
        <Marker position={destinationPoint(lat, lon, 36, 25)} icon={labelIcon(`${Math.round(metrics.windSpeed)} ${copy.summary.wind} · ${copy.summary.gust} ${Math.round(metrics.gustSpeed)}`, "#38bdf8")} />
      </>
    );
  }

  if (activeLayer === "swell") {
    return (
      <>
        {[0.55, 0.85, 1.15].map((factor) => (
          <Circle key={factor} center={center} radius={waveRadius * factor} pathOptions={{ color: "#0891b2", fillOpacity: 0, opacity: 0.72, weight: 2 }} />
        ))}
        {swellLines.map((line, index) => (
          <Polyline key={`swell-${index}`} positions={[line.start, line.end]} pathOptions={{ color: "#06b6d4", weight: 3, opacity: 0.68, dashArray: "3 12" }} />
        ))}
        <Marker position={destinationPoint(lat, lon, 32, metrics.swellDirection)} icon={directionIcon("≈", "#0891b2", metrics.swellDirection, 30)} />
        <Marker position={destinationPoint(lat, lon, 42, 315)} icon={labelIcon(`${metrics.waveHeight.toFixed(1)} ${copy.summary.waves}`, "#67e8f9")} />
      </>
    );
  }

  if (activeLayer === "rain") {
    return (
      <>
        <Circle center={center} radius={rainRadius} pathOptions={{ color: "#2563eb", fillColor: "#3b82f6", fillOpacity: metrics.precipitation > 0 ? 0.16 : 0.06, weight: 2 }} />
        <Marker position={destinationPoint(lat, lon, 36, 45)} icon={labelIcon(`${metrics.precipitation.toFixed(1)} ${copy.summary.rainSignal}`, "#60a5fa")} />
      </>
    );
  }

  if (activeLayer === "lightning") {
    const color = thunderRisk > 55 ? "#dc2626" : thunderRisk > 25 ? "#f59e0b" : "#7c3aed";
    return (
      <>
        <Circle center={center} radius={Math.max(30000, thunderRisk * 900)} pathOptions={{ color, fillColor: color, fillOpacity: 0.14, weight: 2 }} />
        {[40, 165, 285].map((bearing, index) => (
          <Marker key={bearing} position={destinationPoint(lat, lon, 20 + index * 12, bearing)} icon={directionIcon("⚡", color, 0, 28)} />
        ))}
        <Marker position={destinationPoint(lat, lon, 42, 335)} icon={labelIcon(`${thunderRisk}% ${copy.summary.thunderRisk}`, color)} />
      </>
    );
  }

  return (
    <>
      {[28000, 46000, 64000, 82000].map((radius, index) => (
        <Circle key={radius} center={center} radius={radius} pathOptions={{ color: pressureTone, fillOpacity: 0, opacity: 0.7 - index * 0.08, weight: 2, dashArray: index % 2 ? "8 10" : undefined }} />
      ))}
      <Marker position={destinationPoint(lat, lon, 28, 20)} icon={labelIcon(`${Math.round(metrics.pressure)} ${copy.summary.hpa}`, pressureTone)} />
      <Marker position={destinationPoint(lat, lon, 50, 210)} icon={directionIcon(metrics.pressure >= 1013 ? "H" : "L", pressureTone, 0, 32)} />
    </>
  );
}

export default function WeatherIntelligenceMap({ latitude, longitude, forecast, marine, lang = "en" }) {
  const copy = getPublicWeatherTranslations(lang).radar;
  const localizedLayers = useMemo(() => layers.map((layer) => ({ ...layer, label: copy.layers[layer.id] || layer.label })), [copy]);
  const [activeLayer, setActiveLayer] = useState("rain");
  const [radarTime, setRadarTime] = useState(null);
  const lat = Number(latitude || 32.0853);
  const lon = Number(longitude || 34.7818);
  const center = useMemo(() => [lat, lon], [lat, lon]);
  const current = forecast?.current || {};
  const marineCurrent = marine?.current || {};
  const metrics = useMemo(() => ({
    windSpeed: toNumber(current.wind_speed_10m, 0),
    gustSpeed: toNumber(current.wind_gusts_10m, current.wind_speed_10m || 0),
    windDirection: toNumber(current.wind_direction_10m, 225),
    precipitation: toNumber(current.precipitation ?? current.rain, 0),
    pressure: toNumber(current.pressure_msl ?? current.surface_pressure, 1013),
    waveHeight: toNumber(marineCurrent.wave_height, 0.6),
    swellDirection: toNumber(marineCurrent.swell_wave_direction ?? marineCurrent.wave_direction, current.wind_direction_10m || 225),
  }), [current, marineCurrent]);
  const active = localizedLayers.find((layer) => layer.id === activeLayer) || localizedLayers[2];
  const layerSummary = {
    wind: `${Math.round(metrics.windSpeed)} ${copy.summary.wind} · ${copy.summary.gust} ${Math.round(metrics.gustSpeed)}`,
    swell: `${metrics.waveHeight.toFixed(1)} ${copy.summary.waveField}`,
    rain: radarTime ? copy.summary.liveRadar : `${metrics.precipitation.toFixed(1)} ${copy.summary.rainSignal}`,
    lightning: copy.summary.thunderRisk,
    pressure: `${Math.round(metrics.pressure)} ${copy.summary.pressureBands}`,
  }[activeLayer];
  const fullscreenUrl = `https://www.rainviewer.com/map.html?loc=${lat.toFixed(4)},${lon.toFixed(4)},7&oFa=0&oC=1&oU=0&oCS=1&oF=0&oAP=1&c=1&o=83&lm=1&layer=radar&sm=1&sn=1`;
  const markerIcon = useMemo(() => L.divIcon({
    className: "",
    html: '<div style="width:32px;height:32px;border-radius:9999px;background:#0f766e;border:3px solid white;box-shadow:0 12px 28px rgba(15,23,42,.35);display:flex;align-items:center;justify-content:center;color:white;font-weight:900;">•</div>',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -18],
  }), []);

  useEffect(() => {
    fetch("https://api.rainviewer.com/public/weather-maps.json")
      .then((res) => res.json())
      .then((json) => {
        const latest = json?.radar?.past?.[json.radar.past.length - 1]?.time;
        if (latest) setRadarTime(latest);
      })
      .catch(() => {});
  }, []);

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-label uppercase tracking-[0.3em] text-primary">{copy.eyebrow}</p>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground md:text-3xl">{copy.title}</h2>
        </div>
        <Button variant="outline" className="w-full sm:w-auto" onClick={() => window.open(fullscreenUrl, "_blank", "noopener,noreferrer")}><Expand className="h-4 w-4" /> {copy.fullscreen}</Button>
      </div>
      <Card className="overflow-hidden border-border bg-ocean text-white">
        <CardContent className="relative min-h-[420px] p-0">
          <MapContainer
            center={center}
            zoom={7}
            minZoom={3}
            maxZoom={12}
            scrollWheelZoom
            zoomControl={false}
            className="absolute inset-0 !h-full !w-full !bg-sky-100"
            style={{
              height: "100%",
              width: "100%",
              background: "linear-gradient(135deg, #dff7fb 0%, #bdebf3 52%, #91d8e8 100%)",
            }}
          >
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
              maxNativeZoom={19}
            />
            {activeLayer === "rain" && radarTime && (
              <TileLayer
                key={`${activeLayer}-${radarTime}`}
                attribution="Weather radar &copy; RainViewer"
                url={`https://tilecache.rainviewer.com/v2/radar/${radarTime}/256/{z}/{x}/{y}/2/1_1.png`}
                opacity={0.82}
                zIndex={400}
              />
            )}
            <WeatherDataLayer activeLayer={activeLayer} center={center} metrics={metrics} copy={copy} />
            <Marker position={center} icon={markerIcon}>
              <Popup>{copy.selectedLocation}</Popup>
            </Marker>
            <MapResizeFix center={center} />
          </MapContainer>
          <div className="absolute left-4 top-4 z-[500] rounded-2xl border border-white/15 bg-slate-950/70 p-3 text-xs text-cyan-50 shadow-xl backdrop-blur-xl">
            <p className="flex items-center gap-1.5 font-bold"><MapPin className="h-3.5 w-3.5 text-cyan-200" /> {lat.toFixed(3)}, {lon.toFixed(3)}</p>
          </div>
          <div className="absolute right-4 top-4 z-[500] rounded-full border border-white/20 bg-slate-950/75 px-3 py-1 text-xs font-black text-white shadow-xl backdrop-blur-xl">
            {layerSummary}
          </div>
          <div className="absolute bottom-4 left-4 right-4 z-[500] flex flex-col gap-3 rounded-3xl border border-white/15 bg-slate-950/75 p-3 shadow-2xl backdrop-blur-2xl md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2 text-sm"><Layers className="h-4 w-4" /> {copy.layer}: <strong>{active.label}</strong></div>
            <div className="flex gap-2 overflow-x-auto pb-1 md:pb-0">
              {localizedLayers.map((layer) => {
                const Icon = layer.icon;
                return (
                  <button key={layer.id} onClick={() => setActiveLayer(layer.id)} className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-3 py-2 text-xs font-bold leading-none transition ${activeLayer === layer.id ? "bg-white text-ocean" : "bg-white/10 text-white hover:bg-white/20"}`}>
                    <Icon className="h-3.5 w-3.5 shrink-0" /> <span>{layer.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}